import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import config from '../config';
import { toast } from 'react-toastify';
import { Line } from 'react-chartjs-2';
import { Chart, registerables } from 'chart.js'; 
import 'chartjs-adapter-date-fns';  
import { IonIcon } from '@ionic/react';
import { pencilOutline, trashOutline, star } from 'ionicons/icons';
import { 
  IonModal, 
  IonHeader, 
  IonToolbar, 
  IonTitle, 
  IonButtons, 
  IonButton, 
  IonContent, 
  IonItem, 
  IonLabel, 
  IonInput, 
  IonTextarea, 
  IonDatetime, 
  IonSelect, 
  IonSelectOption,
  IonPage,
  IonRouterOutlet
} from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import { Route } from 'react-router-dom';
import MedicalConcernsModal from './MedicalConcernsModal';
import './EditMetrics.css';
import axios from 'axios';

// Register all necessary components
Chart.register(...registerables); 

const EditMetrics = () => {
  const history = useHistory();
  const location = useLocation();
  const apiUrl = config.apiUrl; 

  // Add formatDate function
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Initialize state from localStorage if available
  const [member_id, setMemberId] = useState(() => localStorage.getItem('member_id'));
  const [user_id, setUserId] = useState(() => localStorage.getItem('user_id'));
  const [auth_token, setAuthToken] = useState(() => localStorage.getItem('auth_token'));
  const [assessment_date, setAssessmentDate] = useState(() => localStorage.getItem('assessment_date'));
  const [userFullName, setUserFullName] = useState('');
  const [metrics, setMetrics] = React.useState([]);
  const [historicData, setHistoricData] = React.useState({});
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [authError, setAuthError] = React.useState(null);
  const [isAuthenticating, setIsAuthenticating] = useState(true);
  const [selectedCategory, setSelectedCategory] = React.useState(null);
  const [selectedSection, setSelectedSection] = React.useState(null);
  const [selectedSubsection, setSelectedSubsection] = React.useState(null);
  const [selectedMetricType, setSelectedMetricType] = React.useState(null);
  const [deletingId, setDeletingId] = React.useState(null);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [searchResults, setSearchResults] = React.useState(null);
  const [existingConsiderAdding, setExistingConsiderAdding] = React.useState([]);
  const [filteredConsiderAdding, setFilteredConsiderAdding] = React.useState([]);
  const chartRef = useRef(null);
  const [chartKey, setChartKey] = useState(0); // Add key for forcing re-render
  const [newMetric, setNewMetric] = useState({
    measurement_date: assessment_date || new Date().toISOString().split('T')[0],
    value: '',
    value_sys: '',
    value_dia: '',
    color: 'green',
    rating_text: 'Good'
  });
  const [editingMetric, setEditingMetric] = useState(null);
  const [showConsiderModal, setShowConsiderModal] = useState(false);
  const [showMedicalConcernsModal, setShowMedicalConcernsModal] = useState(false);
  const [medicalConcernId, setMedicalConcernId] = useState(null);
  const [medicalConcernData, setMedicalConcernData] = useState({
    subject: '',
    description: '',
    status: 'Ongoing',
    date_identified: new Date().toISOString().split('T')[0],
    task: '',
    service_provider: '',
    due_date: new Date().toISOString().split('T')[0],
    person_responsible: '',
    metric_types_categories_id: null,
    task_service_provider: '',
    task_id: null,
    task_due_date: new Date().toISOString().split('T')[0],
    task_person_responsible: '',
    task_status: 'Ongoing',
    task_recurring_months: '',
    task_recurring: false,
    task_sent_to_salesforce: false,
    task_assigned_user_name: ''
  });
  const [currentStep, setCurrentStep] = useState(1);
  const [medicalConcerns, setMedicalConcerns] = useState([]);
  const [users, setUsers] = useState([]);
  const [conciergeId, setConciergeId] = useState(null);
  const [selectedMetricsForConcern, setSelectedMetricsForConcern] = useState([]);
  const [showMedicalConcernsSection, setShowMedicalConcernsSection] = useState(false);
  const [memberInfo, setMemberInfo] = useState(null);
  const pageRef = useRef(null);
  const [presentingElement, setPresentingElement] = useState(null);
  const [categoryMedicalConcerns, setCategoryMedicalConcerns] = useState({});
  const initialLoadRef = useRef(false);
  const [hasShownModalForCategory, setHasShownModalForCategory] = useState(false);

  useEffect(() => {
    setPresentingElement(pageRef.current);
  }, []);

  // Persist auth data to localStorage when it changes
  useEffect(() => {
    if (member_id && user_id && auth_token) {
      localStorage.setItem('member_id', member_id);
      localStorage.setItem('user_id', user_id);
      localStorage.setItem('auth_token', auth_token);
      localStorage.setItem('assessment_date', assessment_date); 
      newMetric.measurement_date = assessment_date;
      console.log('New metric measurement date:', newMetric.measurement_date);
      console.log('Assessment date:', assessment_date);
      setIsAuthenticating(false);
    }
  }, [member_id, user_id, auth_token, assessment_date]);

  // Define allowed origins for postMessage
  const allowedOrigins = [
    'https://www.infinity-mc-hms.com',
    'https://infinity-mc-healthplan-edit-dot-infinity-medical-concier-724b0.lm.r.appspot.com',
    'http://localhost:3000',
    'http://localhost:5000',
    'https://localhost:3000',
    'https://localhost:5000'
  ];

  // Add message event listener to receive auth data
  useEffect(() => {
    console.log('Setting up message listener...');
    console.log('Parent window exists:', !!window.parent);
    console.log('Is same origin:', window.parent === window);
    
    const handleMessage = (event) => {
      console.log('Received message:', {
        ...event,
        data: event.data?.target ? { target: event.data.target } : event.data
      });
      
      // Ignore MetaMask and webpack messages
      if (event.data?.target === 'metamask-contentscript' || 
          event.data?.target === 'metamask-inpage' ||
          event.data?.type === 'webpackWarnings') {
        return;
      }

      console.log('Processing message from origin:', event.origin);
      
      // Verify the origin of the message
      if (!allowedOrigins.includes(event.origin)) {
        console.log('Message origin not in allowed list:', event.origin);
        console.log('Allowed origins:', allowedOrigins);
        return;
      }

      // Verify the message structure
      if (event.data && event.data.type === 'AUTH_DATA') {
        const { member_id, user_id, auth_token, assessment_date: incomingAssessmentDate } = event.data;
        console.log('Auth data received:', { 
          member_id, 
          user_id, 
          auth_token: auth_token ? '***' : undefined,
          assessment_date: incomingAssessmentDate
        });
        
        if (member_id && user_id && auth_token) {
          console.log('Setting auth data...');
          setMemberId(member_id);
          setUserId(user_id);
          setAuthToken(auth_token);
          setAssessmentDate(incomingAssessmentDate);
          setAuthError(null);
          console.log('Auth data successfully set');
        } else {
          console.error('Missing required auth data:', { 
            hasMemberId: !!member_id, 
            hasUserId: !!user_id, 
            hasAuthToken: !!auth_token 
          });
          setAuthError('Missing required authentication data. Please ensure you are properly logged in.');
        }
      }
    };

    window.addEventListener('message', handleMessage);
    
    // Request auth data from parent only if we don't have it
    if (!member_id || !user_id || !auth_token) {
      console.log('Requesting auth data from parent...');
      allowedOrigins.forEach(origin => {
        try {
          window.parent.postMessage({ type: 'REQUEST_AUTH' }, origin);
          console.log('Auth request sent to origin:', origin);
        } catch (error) {
          console.error('Error sending auth request to origin:', origin, error);
        }
      });
    } else {
      console.log('Already have auth data, skipping request');
      setIsAuthenticating(false);
    }

    return () => {
      console.log('Cleaning up message listener...');
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  // Add effect to fetch data when we have auth credentials and member info
  useEffect(() => {
    if (!apiUrl) {
      console.error('API URL is not configured');
      setError('API URL is not configured');
      setLoading(false);
      return;
    }

    if (isAuthenticating) {
      console.log('Still authenticating, waiting to fetch data...');
      return;
    }

    if (member_id && user_id && auth_token) {
      console.log('Starting data fetch with:', {
        hasMemberId: !!member_id,
        hasUserId: !!user_id,
        hasAuthToken: !!auth_token,
        hasMemberInfo: !!memberInfo,
        memberSex: memberInfo?.Sex,
        apiUrl
      });
      
      // If we don't have memberInfo, fetch it first
      if (!memberInfo) {
        console.log('No memberInfo, fetching now...');
        fetchMemberInfo();
        return;
      }
      
      // If we have memberInfo, proceed with fetchData
      fetchData();
    } else {
      console.log('Waiting for auth data before fetching...', {
        hasMemberId: !!member_id,
        hasUserId: !!user_id,
        hasAuthToken: !!auth_token,
        hasMemberInfo: !!memberInfo
      });
    }
  }, [member_id, user_id, auth_token, apiUrl, isAuthenticating, memberInfo, assessment_date]);

  // Add effect to log state changes
  useEffect(() => {
    console.log('Auth state changed:', {
      hasMemberId: !!member_id,
      hasUserId: !!user_id,
      hasAuthToken: !!auth_token,
      isLoading: loading,
      hasError: !!error,
      hasAuthError: !!authError
    });
  }, [member_id, user_id, auth_token, loading, error, authError]);

  // Update all API calls to use the auth_token from state
  const fetchHistoricData = async (memberId, retryCount = 0) => {
    try {
      const historicUrl = `${apiUrl}/metrics/${memberId}`;
      console.log('Fetching historic data from:', historicUrl);
      
      const response = await fetch(historicUrl, {
        headers: {
          'Authorization': `Bearer ${auth_token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        const errorData = JSON.parse(errorText);
        
        // If token is expired and we haven't retried yet, wait a bit and retry
        if (errorData.code === 'ERROR_CODE_UNAUTHORIZED' && retryCount < 3) {
          console.log('Token expired, retrying in 1 second...');
          await new Promise(resolve => setTimeout(resolve, 1000));
          return fetchHistoricData(memberId, retryCount + 1);
        }
        
        throw new Error(`Failed to fetch historic data: ${response.status} ${errorText}`);
      }
      
      const historicData = await response.json();
      console.log('Received historic data:', {
        count: historicData?.length || 0,
        firstItem: historicData?.[0]
      });
      
      return historicData;
    } catch (error) {
      console.error('Error fetching historic data:', error);
      throw error;
    }
  };

  const fetchData = async () => {
    try {
      // First ensure we have memberInfo
      if (!memberInfo) {
        console.log('MemberInfo not loaded, waiting for it...');
        return; // Exit early if we don't have member info yet
      }

      console.log('Starting fetchData with memberInfo:', {
        memberId: member_id,
        memberInfo: {
          sex: memberInfo.Sex,
          timestamp: new Date().toISOString()
        }
      });

      const metricsUrl = `${apiUrl}/metric_types_all`;
      console.log('Fetching metric types from:', metricsUrl);
      
      // Fetch metric types
      const typesResponse = await fetch(metricsUrl, {
        headers: {
          'Authorization': `Bearer ${auth_token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      
      if (!typesResponse.ok) {
        const errorText = await typesResponse.text();
        console.error('Failed to fetch metric types:', {
          status: typesResponse.status,
          statusText: typesResponse.statusText,
          errorText,
          url: metricsUrl
        });
        throw new Error(`Failed to fetch metric types: ${typesResponse.status} ${typesResponse.statusText}`);
      }
      
      const typesData = await typesResponse.json(); 
       

      // Filter metrics
      const validMetrics = typesData.filter(metric => {
        const nameValid = metric.name && metric.name.trim() !== '';
        const categoryValid = metric.category_name && metric.category_name.trim() !== '';
        const sectionValid = metric.section_name && metric.section_name.trim() !== '';
        const subsectionValid = !metric.sub_section_name || metric.sub_section_name.trim() !== '';
        
        const sexValid = metric.sex === 'both' || 
                        metric.sex.toLowerCase() === memberInfo.Sex.toLowerCase();

        if (!sexValid) {
          console.log('Metric filtered out due to sex mismatch:', {
            metricName: metric.name,
            metricSex: metric.sex,
            memberSex: memberInfo.Sex,
            memberId: member_id,
            timestamp: new Date().toISOString()
          });
        }

        return nameValid && categoryValid && sectionValid && subsectionValid && sexValid;
      });

      console.log('Filtered metrics result:', {
        totalMetrics: typesData.length,
        validMetrics: validMetrics.length,
        memberId: member_id,
        memberSex: memberInfo.Sex,
        timestamp: new Date().toISOString()
      });

      // Group metrics by category, section, and subsection (if exists)
      const groupedMetrics = validMetrics.reduce((acc, metric) => {
        const categoryKey = metric.category_id;
        const sectionKey = metric.section_id;
        const subsectionKey = metric.sub_section_id;
        
        if (!acc[categoryKey]) {
          acc[categoryKey] = {
            category_name: metric.category_name,
            category_order: metric.category_order,
            category_color: metric.category_color || '#37424A',
            id: metric.category_id,
            sections: {}
          };
        }
        
        if (!acc[categoryKey].sections[sectionKey]) {
          // Check if any metrics in this section have subsections
          const hasSubsections = validMetrics.some(m => 
            m.section_id === sectionKey && m.sub_section_id !== null
          );
          
          acc[categoryKey].sections[sectionKey] = {
            section_name: metric.section_name,
            section_order: metric.section_order,
            has_subsections: hasSubsections,
            subsections: {},
            metrics: []
          };
        }
        
        // Create the metric object
        const metricObj = {
          id: metric.id,
          name: metric.name,
          type: metric.type,
          label: metric.label,
          value_source: metric.value_source,
          guideline_content: metric.guideline_content,
          guideline_type: metric.guideline_type,
          guideline_link: metric.guideline_link,
          guideline_link_description: metric.guideline_link_description,
          is_key_metric: metric.is_key_metric,
          order: metric.order
        };
        
        // If this metric has a subsection, add it to the subsection
        if (subsectionKey) {
          if (!acc[categoryKey].sections[sectionKey].subsections[subsectionKey]) {
            acc[categoryKey].sections[sectionKey].subsections[subsectionKey] = {
              subsection_name: metric.sub_section_name,
              subsection_order: metric.sub_section_order || 0,
              metrics: []
            };
          }
          acc[categoryKey].sections[sectionKey].subsections[subsectionKey].metrics.push(metricObj);
        } else {
          // Add the metric directly to the section
          acc[categoryKey].sections[sectionKey].metrics.push(metricObj);
        }
        
        return acc;
      }, {});

      // After processing all metrics, log the structure
      console.log('Processed metrics structure:', {
        categories: Object.keys(groupedMetrics).length,
        sections: Object.values(groupedMetrics).map(cat => ({
          category: cat.category_name,
          sections: Object.keys(cat.sections).length,
          subsections: Object.values(cat.sections).map(sec => ({
            section: sec.section_name,
            hasSubsections: sec.has_subsections,
            subsectionCount: Object.keys(sec.subsections).length
          }))
        }))
      });

      // Sort categories, sections, subsections, and metrics
      const sortedCategories = Object.values(groupedMetrics)
        .sort((a, b) => a.category_order - b.category_order)
        .map(category => ({
          ...category,
          sections: Object.values(category.sections)
            .sort((a, b) => a.section_order - b.section_order)
            .map(section => {
              if (section.has_subsections) {
                return {
                  ...section,
                  subsections: Object.values(section.subsections)
                    .sort((a, b) => a.subsection_order - b.subsection_order)
                    .map(subsection => ({
                      ...subsection,
                      metrics: subsection.metrics.sort((a, b) => a.order - b.order)
                    }))
                };
              } else {
                return {
                  ...section,
                  metrics: section.metrics.sort((a, b) => a.order - b.order)
                };
              }
            })
        }))
        // Filter out categories with no sections
        .filter(category => category.sections.length > 0)
        // Filter out sections with no metrics and no subsections
        .map(category => ({
          ...category,
          sections: category.sections.filter(section => 
            section.metrics.length > 0 || 
            (section.has_subsections && Object.values(section.subsections).some(sub => sub.metrics.length > 0))
          )
        }))
        // Filter out subsections with no metrics
        .map(category => ({
          ...category,
          sections: category.sections.map(section => ({
            ...section,
            subsections: section.has_subsections ? 
              Object.values(section.subsections)
                .filter(subsection => subsection.metrics.length > 0)
                .reduce((acc, sub) => {
                  acc[sub.subsection_name] = sub;
                  return acc;
                }, {}) : 
              {}
          }))
        }));

      // Fetch historic data with retry mechanism
      const historicData = await fetchHistoricData(member_id);
      
      // Group historic data by metric type
      const groupedHistoricData = historicData.reduce((acc, data) => {
        if (!acc[data.metric_types_id]) {
          acc[data.metric_types_id] = [];
        }
        acc[data.metric_types_id].push(data);
        return acc;
      }, {});

      setMetrics(sortedCategories);
      setHistoricData(groupedHistoricData);
      
      // Set initial selected category, section, and metric type
      if (sortedCategories.length > 0) {
        setSelectedCategory(sortedCategories[0]);
        if (sortedCategories[0].sections.length > 0) {
          const firstSection = sortedCategories[0].sections[0];
          setSelectedSection(firstSection);
          
          if (firstSection.has_subsections) {
            const subsections = Object.values(firstSection.subsections);
            if (subsections.length === 1) {
              const firstSubsection = subsections[0];
              setSelectedSubsection(firstSubsection);
              if (firstSubsection.metrics.length > 0) {
                setSelectedMetricType(firstSubsection.metrics[0]);
              }
            } else if (subsections.length > 0) {
              const firstSubsection = subsections[0];
              setSelectedSubsection(firstSubsection);
              if (firstSubsection.metrics.length > 0) {
                setSelectedMetricType(firstSubsection.metrics[0]);
              }
            } else {
              setSelectedSubsection(null);
              if (firstSection.metrics.length > 0) {
                setSelectedMetricType(firstSection.metrics[0]);
              }
            }
          } else {
            setSelectedSubsection(null);
            if (firstSection.metrics.length > 0) {
              setSelectedMetricType(firstSection.metrics[0]);
            }
          }
        }
      }
      setLoading(false);
    } catch (err) {
      console.error('Error in fetchData:', {
        message: err.message,
        stack: err.stack,
        apiUrl,
        memberId: member_id
      });
      setError(err.message);
      setLoading(false);
    }
  };

  const handleDelete = async (metricId) => {
    try {
      setDeletingId(metricId);
      const response = await fetch(`${apiUrl}/metrics/${metricId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${auth_token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete metric');
      }

      // Update the historic data by removing the deleted metric
      const updatedHistoricData = { ...historicData };
      Object.keys(updatedHistoricData).forEach(metricTypeId => {
        updatedHistoricData[metricTypeId] = updatedHistoricData[metricTypeId].filter(
          data => data.id !== metricId
        );
      });
      setHistoricData(updatedHistoricData);

      // Show success toast
      toast.success('Metric deleted successfully');

    } catch (err) {
      console.error('Error deleting metric:', err);
      setError(err.message);
      toast.error('Failed to delete metric');
    } finally {
      setDeletingId(null);
    }
  };

  const getNextMetric = () => {
    if (!selectedCategory || !selectedSection || !selectedMetricType) return null;

    console.log('Getting next metric for:', {
      category: selectedCategory.category_name,
      section: selectedSection.section_name,
      subsection: selectedSubsection?.subsection_name,
      metric: selectedMetricType.name
    });
 
    const currentSectionIndex = selectedCategory.sections.findIndex(s => s.section_name === selectedSection.section_name);
    
    // If the current section has subsections
    if (selectedSection.has_subsections) {
      if (!selectedSubsection) return null;
      
      const subsections = Object.values(selectedSection.subsections);
      const currentSubsectionIndex = subsections.findIndex(
        s => s.subsection_name === selectedSubsection.subsection_name
      );
      const currentMetricIndex = selectedSubsection.metrics.findIndex(m => m.id === selectedMetricType.id);
       
      // Try to find next metric in current subsection
      if (currentMetricIndex < selectedSubsection.metrics.length - 1) {
        const nextMetric = selectedSubsection.metrics[currentMetricIndex + 1];
        console.log('Found next metric in current subsection:', nextMetric);
        return {
          category: selectedCategory,
          section: selectedSection,
          subsection: selectedSubsection,
          metric: nextMetric
        };
      }
      
      // Try to find next subsection in current section
      if (currentSubsectionIndex < subsections.length - 1) {
        const nextSubsection = subsections[currentSubsectionIndex + 1];
        console.log('Found next subsection:', nextSubsection);
        return {
          category: selectedCategory,
          section: selectedSection,
          subsection: nextSubsection,
          metric: nextSubsection.metrics[0]
        };
      }
      
      // Try to find next section
      if (currentSectionIndex < selectedCategory.sections.length - 1) {
        const nextSection = selectedCategory.sections[currentSectionIndex + 1];
        console.log('Found next section:', nextSection);
        if (nextSection.has_subsections) {
          const firstSubsection = Object.values(nextSection.subsections)[0];
          return {
            category: selectedCategory,
            section: nextSection,
            subsection: firstSubsection,
            metric: firstSubsection.metrics[0]
          };
        } else {
          return {
            category: selectedCategory,
            section: nextSection,
            subsection: null,
            metric: nextSection.metrics[0]
          };
        }
      }
    } else {
      // Regular section without subsections
      const currentMetricIndex = selectedSection.metrics.findIndex(m => m.id === selectedMetricType.id);
       
      // Try to find next metric in current section
      if (currentMetricIndex < selectedSection.metrics.length - 1) {
        const nextMetric = selectedSection.metrics[currentMetricIndex + 1];
        console.log('Found next metric in current section:', nextMetric);
        return {
          category: selectedCategory,
          section: selectedSection,
          subsection: null,
          metric: nextMetric
        };
      }
      
      // Try to find next section
      if (currentSectionIndex < selectedCategory.sections.length - 1) {
        const nextSection = selectedCategory.sections[currentSectionIndex + 1];
        console.log('Found next section:', nextSection);
        if (nextSection.has_subsections) {
          const firstSubsection = Object.values(nextSection.subsections)[0];
          return {
            category: selectedCategory,
            section: nextSection,
            subsection: firstSubsection,
            metric: firstSubsection.metrics[0]
          };
        } else {
          return {
            category: selectedCategory,
            section: nextSection,
            subsection: null,
            metric: nextSection.metrics[0]
          };
        }
      }
    }
    
    // We're at the end of the category, return null
    console.log('Reached end of category:', selectedCategory.category_name);
    return null;
  };

  const getNonGreenMetricsInCategory = (category) => {
    const nonGreenMetrics = [];
    
    category.sections.forEach(section => {
      if (section.has_subsections) {
        Object.values(section.subsections).forEach(subsection => {
          subsection.metrics.forEach(metric => {
            const metricData = historicData[metric.id];
            if (metricData && metricData.length > 0) {
              const latestMetric = metricData[metricData.length - 1];
              if (latestMetric.color !== 'green') {
                nonGreenMetrics.push({
                  ...metric,
                  latestValue: latestMetric
                });
              }
            }
          });
        });
      } else {
        section.metrics.forEach(metric => {
          const metricData = historicData[metric.id];
          if (metricData && metricData.length > 0) {
            const latestMetric = metricData[metricData.length - 1];
            if (latestMetric.color !== 'green') {
              nonGreenMetrics.push({
                ...metric,
                latestValue: latestMetric
              });
            }
          }
        });
      }
    });
    
    return nonGreenMetrics;
  };

  const moveToNextMetric = () => {
    console.log('Moving to next metric...');
    
    // First try to get the next metric
    const nextMetric = getNextMetric();
    
    // If we have a next metric, move to it
    if (nextMetric) {
      console.log('Setting new metric states:', {
        category: nextMetric.category.category_name,
        section: nextMetric.section.section_name,
        subsection: nextMetric.subsection?.subsection_name,
        metric: nextMetric.metric.name
      });
      setSelectedCategory(nextMetric.category);
      setSelectedSection(nextMetric.section);
      setSelectedSubsection(nextMetric.subsection);
      setSelectedMetricType(nextMetric.metric);
      setHasShownModalForCategory(false); // Reset the flag when moving to a new metric

      const metricsContent = document.querySelector('.metrics-content');
      if (metricsContent) {
        metricsContent.scrollIntoView({ behavior: 'smooth' });
      }
      return;
    }

    // If we're here, we're at the end of the category
    console.log('Reached end of category:', selectedCategory.category_name);

    // Check for non-green metrics if we haven't shown the modal yet
    const nonGreenMetrics = getNonGreenMetricsInCategory(selectedCategory);
    if (nonGreenMetrics.length > 0 && !medicalConcernId && !hasShownModalForCategory) {
      // Create description with details of each non-green metric
      const description = nonGreenMetrics.map(metric => {
        const metricData = historicData[metric.id];
        if (metricData && metricData.length > 0) {
          const latestMetric = metricData[metricData.length - 1];
          const date = new Date(latestMetric.measurement_date).toLocaleDateString();
          
          if (metric.type === 'blood_pressure') {
            return `${metric.name} was ${latestMetric.bp_sys}/${latestMetric.bp_dia} mmHg on ${date}`;
          } else if (metric.type === 'yes/no') {
            return `${metric.name} was ${latestMetric.rating_text} on ${date}`;
          } else if (metric.type === 'value') {
            const label = metric.label ? ` ${metric.label}` : '';
            return `${metric.name} was ${latestMetric.value}${label} on ${date}`;
          } else {
            return `${metric.name} was rated as ${latestMetric.rating_text} on ${date}`;
          }
        }
        return '';
      }).filter(desc => desc !== '').join('\n');

      setMedicalConcernData({
        subject: `${selectedCategory.category_name} Concern`,
        description: description,
        date_identified: new Date().toISOString().split('T')[0],
        status: 'Ongoing',
        task: '',
        service_provider: '',
        due_date: new Date().toISOString().split('T')[0],
        person_responsible: '',
        metric_types_categories_id: selectedCategory.id,
        task_service_provider: '',
        task_id: null,
        task_due_date: new Date().toISOString().split('T')[0],
        task_person_responsible: '',
        task_status: 'Ongoing',
        task_recurring_months: '',
        task_recurring: false,
        task_sent_to_salesforce: false,
        task_assigned_user_name: ''
      });
      setCurrentStep(1);
      setShowMedicalConcernsModal(true);
      setHasShownModalForCategory(true); // Set the flag when showing the modal
      window.scrollTo(0, 0);
      return; // Don't proceed to next category until modal is handled
    }

    // If we're here, either there are no non-green metrics or we've already shown the modal
    // Move to the next category
    const currentCategoryIndex = metrics.findIndex(m => m.category_name === selectedCategory.category_name);
    if (currentCategoryIndex < metrics.length - 1) {
      const nextCategory = metrics[currentCategoryIndex + 1];
      const firstSection = nextCategory.sections[0];
      
      // Get the first metric based on whether the section has subsections
      let firstMetric;
      if (firstSection.has_subsections) {
        const firstSubsection = Object.values(firstSection.subsections)[0];
        if (firstSubsection && firstSubsection.metrics.length > 0) {
          firstMetric = firstSubsection.metrics[0];
          setSelectedSubsection(firstSubsection);
        }
      } else if (firstSection.metrics.length > 0) {
        firstMetric = firstSection.metrics[0];
        setSelectedSubsection(null);
      }

      console.log('Setting new category states:', {
        category: nextCategory.category_name,
        section: firstSection.section_name,
        subsection: firstSection.has_subsections ? Object.values(firstSection.subsections)[0]?.subsection_name : null,
        metric: firstMetric?.name
      });

      setSelectedCategory(nextCategory);
      setSelectedSection(firstSection);
      if (firstMetric) {
        setSelectedMetricType(firstMetric);
      }
      setHasShownModalForCategory(false); // Reset the flag when moving to a new category

      // Scroll to the metrics content
      const metricsContent = document.querySelector('.metrics-content');
      if (metricsContent) {
        metricsContent.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleAddTask = async (formData) => {
    try {
      // Find the selected user from the users list
      const assignedUser = users.find(user => user.id === formData.task_person_responsible);
      const assignedUserName = assignedUser ? assignedUser.full_name : 'Not assigned';

      const taskData = {
        medicalconcerns_id: medicalConcernId,
        task: formData.task,
        service_provider: formData.task_service_provider,
        due_date: formData.task_due_date,
        salesforce_users_id: formData.task_person_responsible,
        status: 'Ongoing',
        task_id: formData.task_id,
        task_assigned_user_name: assignedUserName,
        concern_subject: medicalConcernData.subject
      };

      if (taskData.task_id) {
        const taskResponse = await fetch(`${apiUrl}/medicalconcerntasks/${taskData.task_id}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${auth_token}`
          },
          body: JSON.stringify(taskData)
        });
  
        if (!taskResponse.ok) {
          throw new Error('Failed to update medical concern task');
        }
  
        await taskResponse.json();
      } else {
        const taskResponse = await fetch(`${apiUrl}/medicalconcerntasks`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${auth_token}`
          },
          body: JSON.stringify(taskData)
        });

        if (!taskResponse.ok) {
          throw new Error('Failed to add medical concern task');
        }

        await taskResponse.json();
      }

      // Now that both concern and task are saved, refresh the medical concerns
      await fetchMedicalConcerns();

      // Send email notification
      if (memberInfo) {
        await sendTaskEmail(taskData, memberInfo.First_name + ' ' + memberInfo.Surname);
      } else {
        console.log('No member info found. memberInfo:', memberInfo);
      }

      // Reset all modal-related state
      setMedicalConcernData({
        subject: '',
        description: '',
        status: 'Ongoing',
        date_identified: new Date().toISOString().split('T')[0],
        task: '',
        service_provider: '',
        due_date: new Date().toISOString().split('T')[0],
        person_responsible: '',
        metric_types_categories_id: null,
        task_service_provider: '',
        task_id: null,
        task_due_date: new Date().toISOString().split('T')[0],
        task_person_responsible: '',
        task_status: 'Ongoing',
        task_recurring_months: '',
        task_recurring: false,
        task_sent_to_salesforce: false,
        task_assigned_user_name: ''
      });
      setMedicalConcernId(null);
      setCurrentStep(1);
      setShowMedicalConcernsModal(false);

      toast.success('Medical concern and task added successfully');

      // Wait a brief moment for the modal to close before moving to next metric
      setTimeout(() => {
        moveToNextMetric();
      }, 100);
    } catch (error) {
      console.error('Error in handleAddTask:', error);
      toast.error('Failed to add task. Please try again.');
      throw error;
    }
  };

  const handleMedicalConcernChange = useCallback((e) => {
    const { name, value } = e.target;
    setMedicalConcernData(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  const handleTaskChange = useCallback((e) => {
    const { name, value } = e.target;
    setMedicalConcernData(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  const handleAddMedicalConcern = async () => {
    try {
      const concernData = {
        members_id: member_id,
        metric_types_categories_id: medicalConcernData.metric_types_categories_id,
        subject: medicalConcernData.subject,
        description: medicalConcernData.description,
        status: medicalConcernData.status,
        date_identified: medicalConcernData.date_identified
      };

      let response;
      if (medicalConcernId) {
        // Update existing medical concern
        console.log('Updating medical concern:', { medicalConcernId, concernData });
        response = await fetch(`${apiUrl}/medicalconcerns/${medicalConcernId}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${auth_token}`
          },
          body: JSON.stringify(concernData)
        });
      } else {
        // Create new medical concern
        console.log('Creating new medical concern:', concernData);
        response = await fetch(`${apiUrl}/medicalconcerns`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${auth_token}`
          },
          body: JSON.stringify(concernData)
        });
      }

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Medical concern response error:', errorText);
        throw new Error(medicalConcernId ? 'Failed to update medical concern' : 'Failed to add medical concern');
      }

      const result = await response.json();
      console.log('Medical concern response:', result);
      
      if (!medicalConcernId) {
        // Only set the ID if this was a new concern
        setMedicalConcernId(result.id);
      }

      // No need to refresh medical concerns here since we're moving to task step
      setCurrentStep(2);

      // Show success message
      toast.success(medicalConcernId ? 'Medical concern updated successfully' : 'Medical concern added successfully');
    } catch (error) {
      console.error('Error with medical concern:', error);
      toast.error(error.message);
    }
  };

  const getProgress = () => {
    if (!selectedCategory || !selectedSection || !selectedMetricType) return { current: 0, total: 0 };
    
    let totalMetrics = 0;
    let currentMetricIndex = 0;
    let foundCurrent = false;

    metrics.forEach(category => {
      category.sections.forEach(section => {
        if (section.has_subsections) {
          Object.values(section.subsections).forEach(subsection => {
            subsection.metrics.forEach(metric => {
              if (metric.id === selectedMetricType.id) {
                foundCurrent = true;
              }
              if (!foundCurrent) {
                currentMetricIndex++;
              }
              totalMetrics++;
            });
          });
        } else {
          section.metrics.forEach(metric => {
            if (metric.id === selectedMetricType.id) {
              foundCurrent = true;
            }
            if (!foundCurrent) {
              currentMetricIndex++;
            }
            totalMetrics++;
          });
        }
      });
    });

    return {
      current: currentMetricIndex + 1,
      total: totalMetrics
    };
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (!query.trim()) {
      setSearchResults(null);
      return;
    }

    const results = {
      categories: [],
      sections: [],
      subsections: [],
      metrics: []
    };

    metrics.forEach(category => {
      // Search in category name
      if (category.category_name.toLowerCase().includes(query.toLowerCase())) {
        results.categories.push(category);
      }

      // Search in sections
      category.sections.forEach(section => {
        if (section.section_name.toLowerCase().includes(query.toLowerCase())) {
          results.sections.push(section);
        }

        // Search in subsections if they exist
        if (section.has_subsections) {
          Object.values(section.subsections).forEach(subsection => {
            if (subsection.subsection_name.toLowerCase().includes(query.toLowerCase())) {
              results.subsections.push({
                subsection,
                section,
                category
              });
            }

            // Search in metrics within subsections
            subsection.metrics.forEach(metric => {
              if (metric.name.toLowerCase().includes(query.toLowerCase())) {
                results.metrics.push({
                  metric,
                  subsection,
                  section,
                  category
                });
              }
            });
          });
        } else {
          // Search in metrics directly under sections
          section.metrics.forEach(metric => {
            if (metric.name.toLowerCase().includes(query.toLowerCase())) {
              results.metrics.push({
                metric,
                section,
                category
              });
            }
          });
        }
      });
    });

    setSearchResults(results);
  };

  const handleSearchResultClick = (result) => {
    console.log('Search result clicked:', result);
    
    if (result.metric) {
      console.log('Setting category from metric:', result.category);
      setSelectedCategory(result.category);
      console.log('Setting section from metric:', result.section);
      setSelectedSection(result.section);
      if (result.subsection) {
        console.log('Setting subsection from metric:', result.subsection);
        setSelectedSubsection(result.subsection);
      } else {
        setSelectedSubsection(null);
      }
      console.log('Setting metric type:', result.metric);
      setSelectedMetricType(result.metric);
    } else if (result.category) {
      console.log('Setting category:', result.category);
      setSelectedCategory(result.category);
      if (result.category.sections.length > 0) {
        const firstSection = result.category.sections[0];
        console.log('Setting section:', firstSection);
        setSelectedSection(firstSection);
        
        if (firstSection.has_subsections && Object.values(firstSection.subsections).length > 0) {
          const firstSubsection = Object.values(firstSection.subsections)[0];
          console.log('Setting subsection:', firstSubsection);
          setSelectedSubsection(firstSubsection);
          if (firstSubsection.metrics.length > 0) {
            console.log('Setting metric type from subsection:', firstSubsection.metrics[0]);
            setSelectedMetricType(firstSubsection.metrics[0]);
          }
        } else {
          setSelectedSubsection(null);
          if (firstSection.metrics.length > 0) {
            console.log('Setting metric type from section:', firstSection.metrics[0]);
            setSelectedMetricType(firstSection.metrics[0]);
          }
        }
      }
    } else if (result.section) {
      const category = metrics.find(c => 
        c.sections.some(s => s.section_name === result.section.section_name)
      );
      if (category) {
        //console.log('Setting category from section:', category);
        setSelectedCategory(category);
        console.log('Setting section:', result.section);
        setSelectedSection(result.section);
        
        if (result.section.has_subsections && Object.values(result.section.subsections).length > 0) {
          const firstSubsection = Object.values(result.section.subsections)[0];
          //console.log('Setting subsection:', firstSubsection);
          setSelectedSubsection(firstSubsection);
          if (firstSubsection.metrics.length > 0) {
            //console.log('Setting metric type from subsection:', firstSubsection.metrics[0]);
            setSelectedMetricType(firstSubsection.metrics[0]);
          }
        } else {
          setSelectedSubsection(null);
          if (result.section.metrics.length > 0) {
            //console.log('Setting metric type from section:', result.section.metrics[0]);
            setSelectedMetricType(result.section.metrics[0]);
          }
        }
      }
    } else if (result.subsection) {
      //console.log('Setting category from subsection:', result.category);
      setSelectedCategory(result.category);
      //console.log('Setting section from subsection:', result.section);
      setSelectedSection(result.section);
      //console.log('Setting subsection:', result.subsection);
      setSelectedSubsection(result.subsection);
      if (result.subsection.metrics.length > 0) {
        //console.log('Setting metric type from subsection:', result.subsection.metrics[0]);
        setSelectedMetricType(result.subsection.metrics[0]);
      }
    }
    setSearchQuery('');
    setSearchResults(null);
  };

  const getChartData = (metricId) => {
    if (!historicData[metricId] || !selectedMetricType) return null;

    const sortedData = [...historicData[metricId]].sort((a, b) => 
      new Date(a.measurement_date) - new Date(b.measurement_date)
    );

    // Get the color of the most recent metric
    const mostRecentMetric = sortedData[sortedData.length - 1];
    const colorMap = {
      'green': '#28a745',
      'orange': '#fd7e14',
      'red': '#dc3545',
      'blue': '#0056b3',
      'yellow': '#ffc107'
    };
    const mostRecentColor = colorMap[mostRecentMetric?.color] || '#0056b3';

    // Check if this is a blood pressure metric
    if (selectedMetricType.type === 'blood_pressure') {
      return {
        labels: sortedData.map(data => data.measurement_date),
        datasets: [
          {
            label: 'Systolic',
            data: sortedData.map(data => data.bp_sys),
            borderColor: '#dc3545', // Red for systolic
            backgroundColor: 'rgba(220, 53, 69, 0.1)',
            tension: 0.4,
            fill: false,
            pointRadius: 4,
            pointHoverRadius: 6,
            pointBackgroundColor: sortedData.map(data => colorMap[data.color] || '#dc3545'),
            pointBorderColor: sortedData.map(data => colorMap[data.color] || '#dc3545'),
            pointHoverBackgroundColor: sortedData.map(data => colorMap[data.color] || '#dc3545'),
            pointHoverBorderColor: sortedData.map(data => colorMap[data.color] || '#dc3545')
          },
          {
            label: 'Diastolic',
            data: sortedData.map(data => data.bp_dia),
            borderColor: '#0056b3', // Blue for diastolic
            backgroundColor: 'rgba(0, 86, 179, 0.1)',
            tension: 0.4,
            fill: false,
            pointRadius: 4,
            pointHoverRadius: 6,
            pointBackgroundColor: sortedData.map(data => colorMap[data.color] || '#0056b3'),
            pointBorderColor: sortedData.map(data => colorMap[data.color] || '#0056b3'),
            pointHoverBackgroundColor: sortedData.map(data => colorMap[data.color] || '#0056b3'),
            pointHoverBorderColor: sortedData.map(data => colorMap[data.color] || '#0056b3')
          }
        ]
      };
    } else {
      // Regular value metric
      return {
        labels: sortedData.map(data => data.measurement_date),
        datasets: [{
          label: 'Value',
          data: sortedData.map(data => data.value),
          borderColor: mostRecentColor,
          backgroundColor: `${mostRecentColor}20`, // Add 20% opacity
          tension: 0.4,
          fill: true,
          pointRadius: 4,
          pointHoverRadius: 6,
          pointBackgroundColor: sortedData.map(data => colorMap[data.color] || mostRecentColor),
          pointBorderColor: sortedData.map(data => colorMap[data.color] || mostRecentColor),
          pointHoverBackgroundColor: sortedData.map(data => colorMap[data.color] || mostRecentColor),
          pointHoverBorderColor: sortedData.map(data => colorMap[data.color] || mostRecentColor)
        }]
      };
    }
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.dataset.label || '';
            const value = context.parsed.y;
            const date = new Date(context.parsed.x).toLocaleDateString();
            return `${label}: ${value}${selectedMetricType && selectedMetricType.type === 'blood_pressure' ? ' mmHg' : ''} on ${date}`;
          },
          title: function() {
            return selectedMetricType ? `${selectedMetricType.name}` : '';
          }
        }
      }
    },
    scales: {
      x: {
        type: 'time',
        time: {
          unit: 'month',
          displayFormats: {
            month: 'MMM yyyy'
          },
          parser: 'yyyy-MM-dd'
        },
        title: {
          display: true,
          text: 'Date'
        },
        grid: {
          display: false
        }
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: selectedMetricType && selectedMetricType.type === 'blood_pressure' ? 'Blood Pressure (mmHg)' : 'Value'
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        }
      }
    }
  };

  const handleAddMetric = async () => {
    try {
      // Create the base metric data
      const metricData = {
        members_id: member_id,
        metric_types_id: selectedMetricType.id,
        measurement_date: newMetric.measurement_date
      };
      
      // Add type-specific fields
      if (selectedMetricType.type === 'blood_pressure') {
        // For blood pressure, use the special endpoint and include both systolic and diastolic values
        metricData.value_sys = parseFloat(newMetric.value_sys);
        metricData.value_dia = parseFloat(newMetric.value_dia);
        metricData.color = newMetric.color;
        metricData.rating_text = newMetric.color === 'green' ? 'Green' : 
                                 newMetric.color === 'orange' ? 'Orange' : 'Red';
        
        // Use the special blood pressure endpoint
        const response = await fetch(`${config.apiUrl}/metrics_add_bp`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${auth_token}`
          },
          body: JSON.stringify(metricData)
        });
        
        if (!response.ok) {
          throw new Error('Failed to add blood pressure metric');
        }
        
        const result = await response.json();
        const newMetricId = result.id;
        
        // Add the new metric to the historic data
        const updatedHistoricData = { ...historicData };
        if (!updatedHistoricData[selectedMetricType.id]) {
          updatedHistoricData[selectedMetricType.id] = [];
        }
        
        // Create the new metric data for historic data
        const newMetricData = {
          id: newMetricId,
          measurement_date: newMetric.measurement_date,
          bp_sys: parseFloat(newMetric.value_sys),
          bp_dia: parseFloat(newMetric.value_dia),
          color: newMetric.color,
          rating_text: newMetric.color === 'green' ? 'Green' : 
                      newMetric.color === 'orange' ? 'Orange' : 'Red',
                      
        };
        
        updatedHistoricData[selectedMetricType.id].push(newMetricData);
        setHistoricData(updatedHistoricData);
        
        // Reset form, keeping the same date as the last added metric
        setNewMetric({
          measurement_date: newMetric.measurement_date, // Keep the same date
          value: '',
          value_sys: '',
          value_dia: '',
          color: 'green',
          rating_text: selectedMetricType.type === 'yes/no' ? 'No' : 'Good'
        });
        
        toast.success('Metric added successfully');
        return;
      } else if (selectedMetricType.type === 'value') {
        metricData.value = parseFloat(newMetric.value);
        metricData.color = newMetric.color;
        metricData.rating_text = newMetric.color === 'green' ? 'Green' : 
                                 newMetric.color === 'orange' ? 'Orange' : 'Red';
      } else if (selectedMetricType.type === 'yes/no') {
        // For yes/no, use the rating_text directly and set color based on Yes/No
        metricData.rating_text = newMetric.rating_text;
        metricData.color = newMetric.rating_text === 'Yes' ? 'red' : 'green';
      } else {
        // For rating type
        metricData.rating_text = newMetric.rating_text;
        metricData.color = newMetric.color;
      }
      
      console.log('Sending metric data:', metricData);

      const response = await fetch(`${config.apiUrl}/metrics`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth_token}`
        },
        body: JSON.stringify(metricData)
      });

      if (!response.ok) {
        throw new Error('Failed to add metric');
      }

      const result = await response.json();
      const newMetricId = result.id;

      // Add the new metric to the historic data
      const updatedHistoricData = { ...historicData };
      if (!updatedHistoricData[selectedMetricType.id]) {
        updatedHistoricData[selectedMetricType.id] = [];
      }
      
      // Create the new metric data for historic data
      const newMetricData = {
        id: newMetricId,
        measurement_date: newMetric.measurement_date
      };
      
      // Add type-specific fields
      if (selectedMetricType.type === 'value') {
        newMetricData.value = parseFloat(newMetric.value);
        newMetricData.color = newMetric.color;
        newMetricData.rating_text = newMetric.color === 'green' ? 'Green' : 
                                    newMetric.color === 'orange' ? 'Orange' : 'Red';
        // Add label to the value if it exists
        if (selectedMetricType.label) {
          newMetricData.value = `${newMetricData.value} ${selectedMetricType.label}`;
        }
      } else if (selectedMetricType.type === 'yes/no') {
        // For yes/no, use the rating_text directly and set color based on Yes/No
        newMetricData.rating_text = newMetric.rating_text;
        newMetricData.color = newMetric.rating_text === 'Yes' ? 'red' : 'green';
      } else {
        // For rating type
        newMetricData.rating_text = newMetric.rating_text;
        newMetricData.color = newMetric.color;
      }
      
      updatedHistoricData[selectedMetricType.id].push(newMetricData);
      setHistoricData(updatedHistoricData);

      // Reset form, keeping the same date as the last added metric
      setNewMetric({
        measurement_date: newMetric.measurement_date, // Keep the same date
        value: '',
        value_sys: '',
        value_dia: '',
        color: 'green',
        rating_text: selectedMetricType.type === 'yes/no' ? 'No' : 'Good'
      });
    } catch (error) {
      console.error('Error adding metric:', error);
      toast.error('Failed to add metric. Please try again.');
    }
  };

  const handleEdit = (metric) => {
    console.log('Editing metric:', metric);
    setEditingMetric(metric);
    
    // Set the form values based on the metric type
    if (selectedMetricType.type === 'blood_pressure') {
      setNewMetric({
        measurement_date: metric.measurement_date,
        value: '',
        value_sys: metric.bp_sys || '',
        value_dia: metric.bp_dia || '',
        color: metric.color,
        rating_text: metric.rating_text || 'Good'
      });
    } else if (selectedMetricType.type === 'yes/no') {
      // For yes/no metrics, set the rating_text directly
      setNewMetric({
        measurement_date: metric.measurement_date,
        value: '',
        value_sys: '',
        value_dia: '',
        color: metric.color,
        rating_text: metric.rating_text
      });
    } else {
      // For other metric types
      setNewMetric({
        measurement_date: metric.measurement_date,
        value: metric.value || '',
        value_sys: '',
        value_dia: '',
        color: metric.color,
        rating_text: metric.rating_text || 'Good'
      });
    }
  };

  const handleUpdate = async () => {
    try {
      console.log('Updating metric with type:', selectedMetricType.type);
      console.log('Updated metric data:', newMetric);
      
      // Create the base metric data
      const metricData = {
        members_id: member_id,
        metric_types_id: selectedMetricType.id,
        measurement_date: newMetric.measurement_date
      };
      
      // Add type-specific fields
      if (selectedMetricType.type === 'blood_pressure') {
        // For blood pressure, use the special endpoint and include both systolic and diastolic values
        metricData.value_sys = parseFloat(newMetric.value_sys);
        metricData.value_dia = parseFloat(newMetric.value_dia);
        metricData.color = newMetric.color;
        metricData.rating_text = newMetric.color === 'green' ? 'Green' : 
                                 newMetric.color === 'orange' ? 'Orange' : 'Red';
        
        // Use the special blood pressure endpoint
        const response = await fetch(`${config.apiUrl}/metrics_add_bp`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${auth_token}`
          },
          body: JSON.stringify(metricData)
        });
        
        if (!response.ok) {
          throw new Error('Failed to update blood pressure metric');
        }
        
        // Update the metric in historic data
        const updatedHistoricData = { ...historicData };
        const metricIndex = updatedHistoricData[selectedMetricType.id].findIndex(m => m.id === editingMetric.id);
        if (metricIndex !== -1) {
          // Create the updated metric data
          const updatedMetricData = {
            ...editingMetric,
            measurement_date: newMetric.measurement_date,
            bp_sys: parseFloat(newMetric.value_sys),
            bp_dia: parseFloat(newMetric.value_dia),
            color: newMetric.color,
            rating_text: newMetric.color === 'green' ? 'Green' : 
                        newMetric.color === 'orange' ? 'Orange' : 'Red'
          };
          
          updatedHistoricData[selectedMetricType.id][metricIndex] = updatedMetricData;
          setHistoricData(updatedHistoricData);
        }
        
        // Reset form and editing state
        setEditingMetric(null);
        setNewMetric({
          measurement_date: newMetric.measurement_date, // Keep the same date
          value: '',
          value_sys: '',
          value_dia: '',
          color: 'green',
          rating_text: selectedMetricType.type === 'yes/no' ? 'No' : 'Good'
        });
        
        toast.success('Metric updated successfully');
        return;
      } else if (selectedMetricType.type === 'value') {
        metricData.value = parseFloat(newMetric.value);
        metricData.color = newMetric.color;
        metricData.rating_text = newMetric.color === 'green' ? 'Green' : 
                                 newMetric.color === 'orange' ? 'Orange' : 'Red';
      } else if (selectedMetricType.type === 'yes/no') {
        // For yes/no, use the rating_text directly and set color based on Yes/No
        metricData.rating_text = newMetric.rating_text;
        metricData.color = newMetric.rating_text === 'Yes' ? 'red' : 'green';
      } else {
        // For rating type
        metricData.rating_text = newMetric.rating_text;
        metricData.color = newMetric.color;
      }
      
      console.log('Sending update data:', metricData);

      const response = await fetch(`${config.apiUrl}/metrics/${editingMetric.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth_token}`
        },
        body: JSON.stringify(metricData)
      });

      if (!response.ok) {
        throw new Error('Failed to update metric');
      }

      // Update the metric in historic data
      const updatedHistoricData = { ...historicData };
      const metricIndex = updatedHistoricData[selectedMetricType.id].findIndex(m => m.id === editingMetric.id);
      if (metricIndex !== -1) {
        // Create the updated metric data
        const updatedMetricData = {
          ...editingMetric,
          measurement_date: newMetric.measurement_date
        };
        
        // Add type-specific fields
        if (selectedMetricType.type === 'value') {
          updatedMetricData.value = parseFloat(newMetric.value);
          updatedMetricData.color = newMetric.color;
          updatedMetricData.rating_text = newMetric.color === 'green' ? 'Green' : 
                                         newMetric.color === 'orange' ? 'Orange' : 'Red';
        } else if (selectedMetricType.type === 'yes/no') {
          // For yes/no, use the rating_text directly and set color based on Yes/No
          updatedMetricData.rating_text = newMetric.rating_text;
          updatedMetricData.color = newMetric.rating_text === 'Yes' ? 'red' : 'green';
        } else {
          // For rating type
          updatedMetricData.rating_text = newMetric.rating_text;
          updatedMetricData.color = newMetric.color;
        }
        
        updatedHistoricData[selectedMetricType.id][metricIndex] = updatedMetricData;
        setHistoricData(updatedHistoricData);
      }

      // Reset form and editing state
      setEditingMetric(null);
      setNewMetric({
        measurement_date: newMetric.measurement_date, // Keep the same date
        value: '',
        value_sys: '',
        value_dia: '',
        color: 'green',
        rating_text: selectedMetricType.type === 'yes/no' ? 'No' : 'Good'
      });
    } catch (error) {
      console.error('Error updating metric:', error);
      toast.error('Failed to update metric. Please try again.');
    }
  };

  const handleCancelEdit = () => {
    setEditingMetric(null);
    
    // Reset form based on the metric type, but keep the same date
    setNewMetric({
      measurement_date: newMetric.measurement_date, // Keep the same date
      value: '',
      value_sys: '',
      value_dia: '',
      color: 'green',
      rating_text: selectedMetricType.type === 'yes/no' ? 'No' : 'Good'
    });
  };

  // Add this new component for displaying guidelines
  const MetricGuideline = ({ guidelineType, guidelineContent, guidelineLink, guidelineLinkDescription }) => {
    // Remove the early return if no guideline type or content
    // if (!guidelineType || !guidelineContent) return null;

    const renderGuideline = () => {
      if (guidelineType && guidelineContent) {
        switch (guidelineType) {
          case 'table':
            try {
              const tableData = JSON.parse(guidelineContent);
              
              if (!tableData.headers || !Array.isArray(tableData.headers) || 
                  !tableData.rows || !Array.isArray(tableData.rows)) {
                console.error('Invalid table data structure:', tableData);
                return <p className="guideline-text">Error: Invalid table format</p>;
              }

              const tableClass = tableData.headers.length > 4 ? 'guideline-table small-font' : 'guideline-table';
              
              return (
                <table className={tableClass}>
                  <thead>
                    <tr>
                      {tableData.headers.map((header, index) => (
                        <th key={index}>{header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tableData.rows.map((row, rowIndex) => {
                      if (row && typeof row === 'object' && !Array.isArray(row) && row.cells) {
                        const rowColor = row.rowColor || 'clear';
                        const rowClass = rowColor !== 'clear' ? `row-${rowColor}` : '';
                        
                        return (
                          <tr key={rowIndex} className={rowClass}>
                            {row.cells.map((cell, cellIndex) => {
                              const cellColor = row.color && row.color[cellIndex];
                              const cellClass = cellColor ? `cell-${cellColor}` : '';
                              
                              return (
                                <td key={cellIndex} className={cellClass}>
                                  {cell}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      } else if (Array.isArray(row)) {
                        return (
                          <tr key={rowIndex}>
                            {row.map((cell, cellIndex) => (
                              <td key={cellIndex}>{cell}</td>
                            ))}
                          </tr>
                        );
                      } else {
                        console.error('Invalid row format:', row);
                        return (
                          <tr key={rowIndex}>
                            <td colSpan={tableData.headers.length}>Invalid row format</td>
                          </tr>
                        );
                      }
                    })}
                  </tbody>
                </table>
              );
            } catch (error) {
              console.error('Error parsing table data:', error);
              return (
                <div className="guideline-text">
                  <p>Error displaying table. Raw content:</p>
                  <pre>{guidelineContent}</pre>
                </div>
              );
            }
          case 'image':
            return (
              <img 
                src={guidelineContent} 
                alt="Metric guideline" 
                className="guideline-image"
              />
            );
          case 'text':
            return <p className="guideline-text">{guidelineContent}</p>;
          default:
            return null;
        }
      }
      return null;
    };

    return (
      <div className="metric-guideline">
        <h4>Guidelines</h4>
        {renderGuideline()}
        
        {/* Display guideline link if available */}
        {guidelineLink && (
          <div className="guideline-link">
            <a 
              href={guidelineLink} 
              target="_blank" 
              rel="noopener noreferrer"
              className="guideline-link-button"
            >
              {guidelineLinkDescription || 'View External Guidelines'}
            </a>
          </div>
        )}
      </div>
    );
  };

  // Add the ConsiderAddingModal component
  const ConsiderAddingModal = ({ isOpen, onClose, metricTypeName }) => {
    const [testType, setTestType] = useState('');
    const [testName, setTestName] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e) => {
      e.preventDefault();
      
      // Validation
      if (!testType || !testName.trim()) {
        toast.error('Please select a test type and enter a test name');
        return;
      }

      setIsSubmitting(true);
      let url;
      let data;

      try {
        switch (testType) {
          case 'pathology':
            url = `${apiUrl}/bloodwork_repeats`;
            data = {
              members_id: member_id,
              test: testName,
              consider_adding_section: metricTypeName,
              user_id: user_id
            };
            break;
          case 'radiology':
            url = `${apiUrl}/radiology_repeats`;
            data = {
              members_id: member_id,
              test: testName,
              consider_adding_section: metricTypeName,
              user_id: user_id
            };
            break;
          case 'consultation':
          case 'other':
              url = `${apiUrl}/consultations`;
            data = {
              members_id: member_id,
              due_next: new Date().toISOString().split('T')[0],
              concern: testName,
              consider_adding_section: metricTypeName,
              user_id: user_id,
              added_by: 'User'
            };
            break;
          default:
            throw new Error('Invalid test type');
        }

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        });

        if (!response.ok) {
          throw new Error('Failed to add test');
        }

        toast.success('Test added successfully');
        
        // Refresh the list of existing considerations
        const refreshResponse = await fetch(`${apiUrl}/consider_adding/existing/${member_id}`, {
          headers: {
            'Authorization': `Bearer ${auth_token}`
          }
        });
        
        if (refreshResponse.ok) {
          const refreshData = await refreshResponse.json();
          setExistingConsiderAdding(refreshData);
        }
        
        onClose();
      } catch (error) {
        console.error('Error adding test:', error);
        toast.error('Failed to add test. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    };

    if (!isOpen) return null;

    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <h3>Consider Adding Additional Test/Consultation</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="testType">Test Type *</label>
              <select
                id="testType"
                value={testType}
                onChange={(e) => setTestType(e.target.value)}
                required
              >
                <option value="">Select a type</option>
                <option value="pathology">Pathology</option>
                <option value="radiology">Radiology</option>
                <option value="consultation">Consultation</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="testName">Test/Consultation Name *</label>
              <input
                type="text"
                id="testName"
                value={testName}
                onChange={(e) => setTestName(e.target.value)}
                placeholder="Enter test or consultation name"
                required
              />
            </div>
            <div className="modal-actions">
              <button 
                type="button" 
                onClick={onClose}
                className="btn cancel-button"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="btn add-button"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Adding...' : 'Add'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Fetch existing consider adding items
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${apiUrl}/consider_adding/existing/${member_id}`, {
          headers: {
            'Authorization': `Bearer ${auth_token}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch existing consider adding items');
        }
        
        const data = await response.json();
        setExistingConsiderAdding(data);
      } catch (error) {
        console.error('Error fetching existing consider adding items:', error);
        toast.error('Failed to load existing tests/consultations');
      }
    };
    
    // Only fetch when we have authentication and are not in authenticating state
    if (member_id && user_id && auth_token && !isAuthenticating && apiUrl) {
      console.log('Fetching consider adding items with auth data');
      fetchData();
    } else {
      console.log('Waiting for auth data before fetching consider adding items');
    }
  }, [apiUrl, member_id, user_id, auth_token, isAuthenticating]);

  // Filter consider adding items based on the current metric type
  useEffect(() => {
    if (selectedMetricType && existingConsiderAdding.length > 0) {
      const filtered = existingConsiderAdding.filter(item => 
        item.consider_adding_section === selectedMetricType.name
      );
      setFilteredConsiderAdding(filtered);
    } else {
      setFilteredConsiderAdding([]);
    }
  }, [selectedMetricType, existingConsiderAdding]);

  // Update newMetric when selectedMetricType changes
  useEffect(() => {
    if (selectedMetricType) {
      console.log('Selected metric type changed:', selectedMetricType);
      
      // Initialize newMetric based on the selected metric type, but preserve the date
      const currentDate = newMetric.measurement_date || new Date().toISOString().split('T')[0];
      
      if (selectedMetricType.type === 'yes/no') {
        setNewMetric({
          measurement_date: currentDate,
          value: '',
          value_sys: '',
          value_dia: '',
          color: 'green',
          rating_text: 'No' // Default to No for yes/no metrics
        });
      } else if (selectedMetricType.type === 'blood_pressure') {
        setNewMetric({
          measurement_date: currentDate,
          value: '',
          value_sys: '',
          value_dia: '',
          color: 'green',
          rating_text: 'Good'
        });
      } else {
        setNewMetric({
          measurement_date: currentDate,
          value: '',
          value_sys: '',
          value_dia: '',
          color: 'green',
          rating_text: 'Good'
        });
      }
    }
  }, [selectedMetricType]);

  // Add a resize observer to re-render charts when container size changes
  useEffect(() => {
    if (selectedMetricType && chartRef.current) {
      const resizeObserver = new ResizeObserver(entries => {
        // Debounce resize updates using requestAnimationFrame
        window.requestAnimationFrame(() => {
          if (!entries.length) return;
          // Force chart to re-render with new dimensions
          setChartKey(prev => prev + 1);
        });
      });
      
      resizeObserver.observe(chartRef.current);
      
      return () => {
        if (chartRef.current) {
          resizeObserver.unobserve(chartRef.current);
        }
        resizeObserver.disconnect();
      };
    }
  }, [selectedMetricType]);

  // Move fetchMedicalConcerns outside of useEffect for reuse
  const fetchMedicalConcerns = async () => {
    // Only fetch if we don't have any concerns loaded yet
    if (medicalConcerns.length > 0) {
      console.log('Medical concerns already loaded, skipping fetch');
      return;
    }

    try {
      const response = await fetch(`${apiUrl}/medicalconcerns/${member_id}`, {
        headers: {
          'Authorization': `Bearer ${auth_token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch medical concerns');
      }
      
      const data = await response.json();
      
      // Fetch tasks for each medical concern
      const concernsWithTasks = await Promise.all(data.map(async (concern) => { 
          const medicalConcernId = concern.id;
          try {
            const taskResponse = await fetch(`${apiUrl}/medicalconcerntasks/${medicalConcernId}`, {
              headers: {
                'Authorization': `Bearer ${auth_token}`
              }
            });
            
            if (taskResponse.ok) {
              const taskData = await taskResponse.json();
              // Check if there are any tasks
              if (taskData && taskData.length > 0) {
                const firstTaskData = taskData[0];
                // Add task data directly to the concern object
                return {
                  ...concern,
                  task: firstTaskData.task,
                  task_id: firstTaskData.id,
                  task_service_provider: firstTaskData.service_provider,
                  task_due_date: firstTaskData.due_date,
                  task_person_responsible: firstTaskData.salesforce_users_id,
                  task_status: firstTaskData.status,
                  task_recurring_months: firstTaskData.recurring_months,
                  task_recurring: firstTaskData.recurring,
                  task_sent_to_salesforce: taskData.task_sent_to_salesforce,
                  task_assigned_user_name: taskData.assigned_user_name
                };
              }
            }
            // If no tasks or error, return concern without task data
            return {
              ...concern,
              task: '',
              task_id: null,
              task_service_provider: '',
              task_due_date: new Date().toISOString().split('T')[0],
              task_person_responsible: '',
              task_status: 'Ongoing',
              task_recurring_months: '',
              task_recurring: false,
              task_sent_to_salesforce: false,
              task_assigned_user_name: ''
            };
          } catch (error) {
            console.error(`Error fetching task for concern ${concern.id}:`, error);
            // Return concern without task data if there's an error
            return {
              ...concern,
              task: '',
              task_id: null,
              task_service_provider: '',
              task_due_date: new Date().toISOString().split('T')[0],
              task_person_responsible: '',
              task_status: 'Ongoing',
              task_recurring_months: '',
              task_recurring: false,
              task_sent_to_salesforce: false,
              task_assigned_user_name: ''
            };
          } 
      }));

      // Group medical concerns by category_id and keep only the most recent one
      const concernsByCategory = concernsWithTasks.reduce((acc, concern) => {
        const categoryId = concern.metric_types_categories_id;
        if (!acc[categoryId] || new Date(concern.date_identified) > new Date(acc[categoryId].date_identified)) {
          acc[categoryId] = concern;
        }
        return acc;
      }, {});

      setCategoryMedicalConcerns(concernsByCategory);
      setMedicalConcerns(concernsWithTasks);
    } catch (error) {
      console.error('Error fetching medical concerns:', error);
      toast.error('Failed to load medical concerns');
    }
  };

  // Add this new function to fetch member information
  const fetchMemberInfo = async () => {
    try {
      console.log('Starting fetchMemberInfo...');
      const response = await fetch(`${apiUrl}/members_highlevel/${member_id}`, {
        headers: {
          'Authorization': `Bearer ${auth_token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch member information');
      }
      
      const data = await response.json(); 
      console.log('Fetched member info:', {
      memberId: member_id,
        sex: data.Sex,
      timestamp: new Date().toISOString()
    });
      setMemberInfo(data);
      return data;
    } catch (error) {
      console.error('Error fetching member information:', error);
      toast.error('Failed to load member information');
      throw error;
    }
  };

  // Update the useEffect that handles initial data loading
  useEffect(() => {
    const loadMedicalConcerns = async () => {
      if (!member_id || !auth_token || isAuthenticating) return;
      if (initialLoadRef.current) return; // Skip if already loaded

      console.log('Initial fetch of medical concerns');
      initialLoadRef.current = true;
      await fetchMedicalConcerns();
    };

    loadMedicalConcerns();
  }, [member_id, auth_token, isAuthenticating]);

  // Fetch users
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await fetch(`${apiUrl}/users_salesforce`, {
          headers: {
            'Authorization': `Bearer ${auth_token}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch users');
        }
        
        const data = await response.json();
        setUsers(data);
      } catch (error) {
        console.error('Error fetching users:', error);
        toast.error('Failed to load users');
      }
    };

    if (auth_token && !isAuthenticating) {
      fetchUsers();
    }
  }, [apiUrl, auth_token, isAuthenticating]);

  // Fetch concierge ID
  useEffect(() => {
    const fetchConciergeId = async () => {
      try {
        const response = await fetch(`${apiUrl}/members/conciergeid/${member_id}`, {
          headers: {
            'Authorization': `Bearer ${auth_token}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch concierge ID');
        }
        
        const data = await response.json();
        setConciergeId(data);
      } catch (error) {
        console.error('Error fetching concierge ID:', error);
        toast.error('Failed to load concierge information');
      }
    };

    if (member_id && auth_token && !isAuthenticating) {
      fetchConciergeId();
    }
  }, [apiUrl, member_id, auth_token, isAuthenticating]);

  const handleMedicalConcernsModalClose = () => {
    console.log('Modal closing...');
    setShowMedicalConcernsModal(false);
    
    // If we're at the end of the category and have shown the modal, move to next category
    if (!getNextMetric() && hasShownModalForCategory) {
      console.log('Reached end of category, moving to next category...');
      const currentCategoryIndex = metrics.findIndex(m => m.category_name === selectedCategory.category_name);
      if (currentCategoryIndex < metrics.length - 1) {
        const nextCategory = metrics[currentCategoryIndex + 1];
        const firstSection = nextCategory.sections[0];
        
        // Get the first metric based on whether the section has subsections
        let firstMetric;
        if (firstSection.has_subsections) {
          const firstSubsection = Object.values(firstSection.subsections)[0];
          if (firstSubsection && firstSubsection.metrics.length > 0) {
            firstMetric = firstSubsection.metrics[0];
            setSelectedSubsection(firstSubsection);
          }
        } else if (firstSection.metrics.length > 0) {
          firstMetric = firstSection.metrics[0];
          setSelectedSubsection(null);
        }

        console.log('Setting new category states:', {
          category: nextCategory.category_name,
          section: firstSection.section_name,
          subsection: firstSection.has_subsections ? Object.values(firstSection.subsections)[0]?.subsection_name : null,
          metric: firstMetric?.name
        });

        setSelectedCategory(nextCategory);
        setSelectedSection(firstSection);
        if (firstMetric) {
          setSelectedMetricType(firstMetric);
        }
        setHasShownModalForCategory(false); // Reset the flag when moving to a new category

        // Scroll to the metrics content
        const metricsContent = document.querySelector('.metrics-content');
        if (metricsContent) {
          metricsContent.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  };

  const sendTaskEmail = async (taskData, memberName) => {
    try {
      const auth = {
        username: 'api',
        password: config.mailgunApiKey,
      };

      const email_body = `A new task has been added to the health plan for ${memberName}.\n\n` +
        `Task type: Medical Concern\n` +
        `Medical Concern: ${taskData.concern_subject}\n` +
        `Task Description: ${taskData.task}\n` +
        `Service Provider: ${taskData.service_provider}\n` +
        `Date: ${taskData.due_date}\n` +
        `Assigned to: ${taskData.task_assigned_user_name || 'Not assigned'}\n` +
        `Added by: ${userFullName || 'System'}\n`;

      const email_body_html = `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
          <h2 style="color: #37424A;">New Task Added</h2>
          <p style="font-size: 16px;">A new task has been added to the health plan for <strong>${memberName}</strong></p>
          
          <table style="width: 100%; border-collapse: collapse; margin: 20px 0;">
            <tr>
              <td style="padding: 8px; border-bottom: 1px solid #eee; color: #37424A;"><strong>Task type:</strong></td>
              <td style="padding: 8px; border-bottom: 1px solid #eee;">Medical Concern</td>
            </tr>
            <tr>
              <td style="padding: 8px; border-bottom: 1px solid #eee; color: #37424A;"><strong>Medical Concern:</strong></td>
              <td style="padding: 8px; border-bottom: 1px solid #eee;">${taskData.concern_subject}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border-bottom: 1px solid #eee; color: #37424A;"><strong>Task Description:</strong></td>
              <td style="padding: 8px; border-bottom: 1px solid #eee;">${taskData.task}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border-bottom: 1px solid #eee; color: #37424A;"><strong>Service Provider:</strong></td>
              <td style="padding: 8px; border-bottom: 1px solid #eee;">${taskData.service_provider}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border-bottom: 1px solid #eee; color: #37424A;"><strong>Date:</strong></td>
              <td style="padding: 8px; border-bottom: 1px solid #eee;">${formatDate(taskData.due_date)}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border-bottom: 1px solid #eee; color: #37424A;"><strong>Assigned to:</strong></td>
              <td style="padding: 8px; border-bottom: 1px solid #eee;">${taskData.task_assigned_user_name || 'Not assigned'}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border-bottom: 1px solid #eee; color: #37424A;"><strong>Added by:</strong></td>
              <td style="padding: 8px; border-bottom: 1px solid #eee;">${userFullName || 'System'}</td>
            </tr>
          </table>
        </div>`;

      const data = new URLSearchParams();
      data.append('from', 'HMS App <hms@infinity-mc-hms.com>');
      data.append('to', 'antony@infinity-mc.com, david@infinity-mc.com, kunesha@infinity-mc.com, schantell@infinity-mc.com');
      data.append('subject', `New task added to ${memberName}'s health plan`);
      data.append('text', email_body);
      data.append('html', email_body_html);

      const response = await axios.post(config.mailgunBaseUrl, data, { auth });
      console.log('Task email sent successfully:', response.data);
    } catch (error) {
      console.error('Failed to send task email:', error);
    }
  };

  // Add function to fetch user's full name
  const fetchUserFullName = async (userId) => {
    try {
      const response = await fetch(`${apiUrl}/users/get_fullname/${userId}`, {
        headers: {
          'Authorization': `Bearer ${auth_token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
       
      if (!response.ok) {
        throw new Error('Failed to fetch user full name');
      }
      const data = await response.json();
      if (data && data.length > 0) {
        setUserFullName(data[0].full_name);
      }
    } catch (error) {
      console.error('Error fetching user full name:', error);
    }
  };

  // Add effect to fetch user's full name when user_id is available
  useEffect(() => {
    if (user_id) {
      fetchUserFullName(user_id);
    }
  }, [user_id]);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;
  if (authError) return (
    <div className="auth-error">
      <h2>Authentication Error</h2>
      <p>{authError}</p>
      <p>Please contact support if this issue persists.</p>
    </div>
  );

  return (
    <div className="edit-metrics-container">
      <style>
        {`
          .category-tab.active {
            background-color: ${selectedCategory?.category_color || '#37424A'};
            color: white;
          }

          .category-tab.active::after {
            content: '';
            position: absolute;
            bottom: -8px;
            left: 50%;
            transform: translateX(-50%);
            width: 0;
            height: 0;
            border-left: 8px solid transparent;
            border-right: 8px solid transparent;
            border-top: 8px solid ${selectedCategory?.category_color || '#37424A'};
          }

          .section-tab.active,
          .subsection-tab.active,
          .metric-type-tab.active {
            background-color: ${selectedCategory?.category_color || '#37424A'};
            color: white;
          }

          .section-tab:hover,
          .subsection-tab:hover,
          .metric-type-tab:hover {
            background-color: ${selectedCategory?.category_color ? `${selectedCategory.category_color}20` : '#e9ecef'};
          }
        `}
      </style>
      <IonPage>
        <div className="edit-metrics" ref={pageRef}>
          {/* Search Bar */}
          <div className="search-container">
            <input
              type="text"
              placeholder="Search categories, sections, or parameters..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              className="search-input"
            />
            {searchResults && (
              <div className="search-results">
                {searchResults.categories.length > 0 && (
                  <div className="search-result-group">
                    <h3>Categories</h3>
                    {searchResults.categories.map(category => (
                      <div
                        key={category.category_name}
                        className="search-result-item"
                        onClick={() => handleSearchResultClick({ category })}
                      >
                        {category.category_name}
                      </div>
                    ))}
                  </div>
                )}
                {searchResults.sections.length > 0 && (
                  <div className="search-result-group">
                    <h3>Sections</h3>
                    {searchResults.sections.map(section => (
                      <div
                        key={section.section_name}
                        className="search-result-item"
                        onClick={() => handleSearchResultClick({ section })}
                      >
                        {section.section_name}
                      </div>
                    ))}
                  </div>
                )}
                {searchResults.subsections.length > 0 && (
                  <div className="search-result-group">
                    <h3>Subsections</h3>
                    {searchResults.subsections.map((result, index) => (
                      <div
                        key={`${result.subsection.subsection_name}-${index}`}
                        className="search-result-item"
                        onClick={() => handleSearchResultClick(result)}
                      >
                        {result.subsection.subsection_name}
                      </div>
                    ))}
                  </div>
                )}
                {searchResults.metrics.length > 0 && (
                  <div className="search-result-group">
                    <h3>Metrics</h3>
                    {searchResults.metrics.map((result, index) => (
                      <div
                        key={`${result.metric.id}-${index}`}
                        className="search-result-item"
                        onClick={() => handleSearchResultClick(result)}
                      >
                        {result.metric.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Tab Navigation */}
          <div className="tab-navigation">
            <div className="tab-level">
              <div className="tab-level-label">Category</div>
              <div className="category-tabs">
                {metrics.map(category => (
                  <button
                    key={category.category_name}
                    className={`category-tab ${selectedCategory?.category_name === category.category_name ? 'active' : ''}`}
                    onClick={() => {
                      console.log('Category clicked:', {
                        name: category.category_name,
                        sections: category.sections.map(s => ({
                          name: s.section_name,
                          hasSubsections: s.has_subsections,
                          subsectionCount: Object.keys(s.subsections).length
                        }))
                      });
                      
                      setSelectedCategory(category);
                      if (category.sections.length > 0) {
                        const firstSection = category.sections[0];
                        console.log('Setting first section:', {
                          name: firstSection.section_name,
                          hasSubsections: firstSection.has_subsections,
                          subsectionCount: Object.keys(firstSection.subsections).length
                        });
                        
                        setSelectedSection(firstSection);
                        
                        if (firstSection.has_subsections) {
                          const subsections = Object.values(firstSection.subsections);
                          if (subsections.length > 0) {
                            const firstSubsection = subsections[0];
                            console.log('Setting first subsection:', firstSubsection);
                            setSelectedSubsection(firstSubsection);
                            if (firstSubsection.metrics.length > 0) {
                              setSelectedMetricType(firstSubsection.metrics[0]);
                            }
                          } else {
                            setSelectedSubsection(null);
                            if (firstSection.metrics.length > 0) {
                              setSelectedMetricType(firstSection.metrics[0]);
                            }
                          }
                        } else {
                          setSelectedSubsection(null);
                          if (firstSection.metrics.length > 0) {
                            setSelectedMetricType(firstSection.metrics[0]);
                          }
                        }
                      }
                    }}
                  >
                    {category.category_name}
                  </button>
                ))}
              </div>
            </div>

            {selectedCategory && (
              <div className="tab-level">
                <div className="tab-level-label">Section</div>
                <div className="section-tabs">
                  {selectedCategory.sections.map(section => (
                    <button
                      key={section.section_name}
                      className={`section-tab ${selectedSection?.section_name === section.section_name ? 'active' : ''}`}
                      onClick={() => {
                        
                        setSelectedSection(section);
                        
                        // Check if there are any subsections by looking at the object keys
                        const hasSubsections = Object.keys(section.subsections || {}).length > 0; 
                        
                        if (hasSubsections) {
                          const subsections = Object.values(section.subsections); 
                          
                          if (subsections.length > 0) {
                            const firstSubsection = subsections[0];
                            console.log('Setting first subsection:', firstSubsection);
                            setSelectedSubsection(firstSubsection);
                            if (firstSubsection.metrics.length > 0) { 
                              setSelectedMetricType(firstSubsection.metrics[0]);
                            }
                          } else { 
                            setSelectedSubsection(null);
                            if (section.metrics.length > 0) {
                              setSelectedMetricType(section.metrics[0]);
                            }
                          }
                        } else { 
                          setSelectedSubsection(null);
                          if (section.metrics.length > 0) {
                            setSelectedMetricType(section.metrics[0]);
                          }
                        }
                      }}
                    >
                      {section.section_name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Only show subsection tab level if the selected section has subsections */}
            {selectedSection && selectedSection.has_subsections && (
              <div className="tab-level">
                <div className="tab-level-label">Subsection</div>
                <div className="subsection-tabs">
                  {Object.values(selectedSection.subsections).map(subsection => (
                    <button
                      key={subsection.subsection_name}
                      className={`subsection-tab ${selectedSubsection?.subsection_name === subsection.subsection_name ? 'active' : ''}`}
                      onClick={() => {
                        setSelectedSubsection(subsection);
                        if (subsection.metrics.length > 0) {
                          setSelectedMetricType(subsection.metrics[0]);
                        }
                      }}
                    >
                      {subsection.subsection_name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {selectedSection && (
              <div className="tab-level">
                <div className="tab-level-label">Parameter Type</div>
                <div className="metric-type-tabs">
                  {selectedSubsection ? 
                    // If we have a selected subsection, show metrics from that subsection
                    selectedSubsection.metrics.map(metric => (
                      <button
                        key={metric.id}
                        className={`metric-type-tab ${selectedMetricType?.id === metric.id ? 'active' : ''}`}
                        onClick={() => setSelectedMetricType(metric)}
                      >
                        {metric.name}
                        {metric.is_key_metric && (
                          <IonIcon 
                            icon={star} 
                            style={{ 
                              marginLeft: '4px',
                              fontSize: '14px',
                              verticalAlign: 'text-bottom',
                              position: 'relative',
                              top: '-1px',
                              color: selectedMetricType?.id === metric.id ? 'white' : '#ffc107'
                            }}
                          />
                        )}
                      </button>
                    ))
                    :
                    // Otherwise, show metrics directly from the section
                    selectedSection.metrics.map(metric => (
                      <button
                        key={metric.id}
                        className={`metric-type-tab ${selectedMetricType?.id === metric.id ? 'active' : ''}`}
                        onClick={() => setSelectedMetricType(metric)}
                      >
                        {metric.name}
                        {metric.is_key_metric && (
                          <IonIcon 
                            icon={star} 
                            style={{ 
                              marginLeft: '4px',
                              fontSize: '14px',
                              verticalAlign: 'text-bottom',
                              position: 'relative',
                              top: '-1px',
                              color: selectedMetricType?.id === metric.id ? 'white' : '#ffc107'
                            }}
                          />
                        )}
                      </button>
                    ))
                  }
                </div>
              </div>
            )}
          </div>

          {/* Metrics Content */}
          {selectedMetricType && (
            <div className="metrics-content">
              <h2>
                <span className="metric-icon">{selectedMetricType.name.charAt(0)}</span>
                {selectedMetricType.name}
              </h2>
              
              {/* Display the guideline if available */}
              <MetricGuideline 
                guidelineType={selectedMetricType.guideline_type}
                guidelineContent={selectedMetricType.guideline_content}
                guidelineLink={selectedMetricType.guideline_link}
                guidelineLinkDescription={selectedMetricType.guideline_link_description}
              />
              
              {/* Quick Entry Form */}
              {selectedMetricType && (
                <div className="quick-entry-form">
                  <h3>{editingMetric ? 'Edit Measurement' : 'Add New Measurement'}</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Date</label>
                      <input
                        type="date"
                        value={newMetric.measurement_date}
                        onChange={(e) => setNewMetric({ ...newMetric, measurement_date: e.target.value })}
                      />
                    </div>

                    {selectedMetricType.type === 'value' ? (
                      <>
                        <div className="form-group">
                          <label>Value</label>
                          <input
                            type="number"
                            value={newMetric.value}
                            onChange={(e) => setNewMetric({ ...newMetric, value: e.target.value })}
                            placeholder={selectedMetricType.value_source || "Enter value"}
                          />
                        </div>
                        <div className="form-group">
                          <label>Color Rating</label>
                          <select
                            value={newMetric.color}
                            onChange={(e) => setNewMetric({ ...newMetric, color: e.target.value })}
                          >
                            <option value="green">Green</option>
                            <option value="orange">Orange</option>
                            <option value="red">Red</option>
                          </select>
                        </div>
                      </>
                    ) : selectedMetricType.type === 'blood_pressure' ? (
                      <>
                        <div className="form-group">
                          <label>Systolic (mmHg)</label>
                          <input
                            type="number"
                            value={newMetric.value_sys}
                            onChange={(e) => setNewMetric({ ...newMetric, value_sys: e.target.value })}
                            placeholder="Enter systolic value"
                          />
                        </div>
                        <div className="form-group">
                          <label>Diastolic (mmHg)</label>
                          <input
                            type="number"
                            value={newMetric.value_dia}
                            onChange={(e) => setNewMetric({ ...newMetric, value_dia: e.target.value })}
                            placeholder="Enter diastolic value"
                          />
                        </div>
                        <div className="form-group">
                          <label>Color Rating</label>
                          <select
                            value={newMetric.color}
                            onChange={(e) => setNewMetric({ ...newMetric, color: e.target.value })}
                          >
                            <option value="green">Green</option>
                            <option value="orange">Orange</option>
                            <option value="red">Red</option>
                          </select>
                        </div>
                      </>
                    ) : selectedMetricType.type === 'yes/no' ? (
                      <div className="form-group">
                        <label>Yes/No</label>
                        <select
                          value={newMetric.rating_text}
                          onChange={(e) => {
                            const rating = e.target.value;
                            console.log('Selected Yes/No value:', rating);
                            // For yes/no metrics, Yes = red, No = green
                            const colorMap = {
                              'Yes': 'red',
                              'No': 'green'
                            };
                            const newColor = colorMap[rating];
                            console.log('Setting color to:', newColor);
                            setNewMetric({
                              ...newMetric,
                              rating_text: rating,
                              color: newColor
                            });
                          }}
                        >
                          <option value="Yes">Yes</option>
                          <option value="No">No</option>
                        </select>
                      </div>
                    ) : (
                      <div className="form-group">
                        <label>Rating</label>
                        <select
                          value={newMetric.rating_text}
                          onChange={(e) => {
                            const rating = e.target.value;
                            const colorMap = {
                              'Good': 'green',
                              'Average': 'orange',
                              'Poor': 'red'
                            };
                            setNewMetric({
                              ...newMetric,
                              rating_text: rating,
                              color: colorMap[rating]
                            });
                          }}
                        >
                          <option value="Good">Good</option>
                          <option value="Average">Average</option>
                          <option value="Poor">Poor</option>
                        </select>
                      </div>
                    )}

                    <div className="form-actions">
                      {editingMetric && (
                        <button className="cancel-button" onClick={handleCancelEdit}>
                          Cancel
                        </button>
                      )}
                      <button 
                        className="add-button" 
                        onClick={editingMetric ? handleUpdate : handleAddMetric}
                      >
                        {editingMetric ? 'Update Measurement' : 'Add Measurement'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {historicData[selectedMetricType.id] && (
                <table className="metric-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      {selectedMetricType.type === 'value' && <th>Value</th>}
                      {selectedMetricType.type === 'blood_pressure' && (
                        <>
                          <th>Systolic</th>
                          <th>Diastolic</th>
                        </>
                      )}
                      <th>Rating</th>
                      <th style={{width: '110px'}}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historicData[selectedMetricType.id].map((data, index) => (
                      <tr key={`${data.id}-${index}`} className={`rating-${data.color}`}>
                        <td>{new Date(data.measurement_date).toLocaleDateString()}</td>
                        {selectedMetricType.type === 'value' && (
                          <td>{data.value}{data.label ? ` ${data.label}` : ''}</td>
                        )}
                        {selectedMetricType.type === 'blood_pressure' && (
                          <>
                            <td>{data.bp_sys} mmHg</td>
                            <td>{data.bp_dia} mmHg</td>
                          </>
                        )}
                        <td>{data.rating_text}</td>
                        <td>
                          <div className="action-buttons">
                            <button
                              className="edit-button"
                              onClick={() => handleEdit(data)}
                              title="Edit this metric"
                            >
                              <IonIcon icon={pencilOutline}></IonIcon>
                            </button>
                            <button
                              className="delete-button"
                              onClick={() => handleDelete(data.id)}
                              disabled={deletingId === data.id}
                              title="Delete this metric"
                            >
                              {deletingId === data.id ? 'Deleting...' : <IonIcon icon={trashOutline}></IonIcon>}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              
              {/* Chart Section */}
              {selectedMetricType && 
               (selectedMetricType.type === 'value' || selectedMetricType.type === 'blood_pressure') && 
               historicData[selectedMetricType.id] && (
                <div className="chart-container" ref={chartRef}>
                  <Line 
                    key={chartKey}
                    data={getChartData(selectedMetricType.id)} 
                    options={chartOptions}
                    height={300}
                  />
                </div>
              )}

              {/* Existing Consider Adding Section */}
              {filteredConsiderAdding.length > 0 && (
                <div className="existing-consider-adding-section">
                  <h3>Existing Tests/Consultations</h3>
                  <div className="existing-consider-adding-list">
                    {filteredConsiderAdding.map(item => (
                      <div key={item.id} className="existing-consider-adding-item">
                        <div className="existing-consider-adding-details">
                          <span className="existing-consider-adding-test">{item.test}</span>
                          <span className="existing-consider-adding-category">{item.category}</span>
                        </div>
                        {item.consider_adding_disease && (
                          <div className="existing-consider-adding-disease">
                            <span className="disease-label">Disease:</span> {item.consider_adding_disease}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Consider Adding Button */}
              <div className="consider-adding-section">
                <button 
                  className="btn btn-secondary consider-adding-button"
                  onClick={() => setShowConsiderModal(true)}
                >
                  Add Related Test or Consultation
                </button>
              </div>

              {/* Medical Concerns Button */}
              <div className="medical-concerns-section" style={{ marginTop: '20px', marginBottom: '20px' }}>
                <button 
                  className="btn btn-primary"
                  onClick={() => {
                    const metricsContent = document.querySelector('.metrics-content');
                    if (metricsContent) {
                      metricsContent.scrollIntoView({ behavior: 'smooth' });
                    }
                    if (!selectedCategory) {
                      console.error('No category selected');
                      toast.error('Please select a category first');
                      return;
                    }

                    const existingConcern = categoryMedicalConcerns[selectedCategory.id];
                    
                    if (existingConcern) {
                      // Load existing concern data 
                      setMedicalConcernData({
                        subject: existingConcern.subject,
                        description: existingConcern.description,
                        status: existingConcern.status,
                        date_identified: existingConcern.date_identified,
                        task: existingConcern.task || '',
                        service_provider: existingConcern.service_provider || '',
                        due_date: existingConcern.due_date || new Date().toISOString().split('T')[0],
                        person_responsible: existingConcern.person_responsible || '',
                        metric_types_categories_id: existingConcern.metric_types_categories_id,
                        task_service_provider: existingConcern.task_service_provider || '',
                        task_id: existingConcern.task_id || null,
                        task_due_date: existingConcern.task_due_date || new Date().toISOString().split('T')[0],
                        task_person_responsible: existingConcern.task_person_responsible || '',
                        task_status: existingConcern.task_status || 'Ongoing',
                        task_recurring_months: existingConcern.task_recurring_months || '',
                        task_recurring: existingConcern.task_recurring || false,
                        task_sent_to_salesforce: existingConcern.task_sent_to_salesforce || false,
                        task_assigned_user_name: existingConcern.task_assigned_user_name || ''
                      });
                      setMedicalConcernId(existingConcern.id);
                    } else {
                      // Create description with details of each non-green metric
                      const nonGreenMetrics = getNonGreenMetricsInCategory(selectedCategory);
                      const description = nonGreenMetrics.map(metric => {
                        const metricData = historicData[metric.id];
                        if (metricData && metricData.length > 0) {
                          const latestMetric = metricData[metricData.length - 1];
                          if (!latestMetric) return '';
                          
                          const date = new Date(latestMetric.measurement_date || new Date()).toLocaleDateString();
                          
                          if (metric.type === 'blood_pressure') {
                            return `${metric.name} was ${latestMetric.bp_sys || 'N/A'}/${latestMetric.bp_dia || 'N/A'} mmHg on ${date}`;
                          } else if (metric.type === 'yes/no') {
                            return `${metric.name} was ${latestMetric.rating_text || 'N/A'} on ${date}`;
                          } else if (metric.type === 'value') {
                            const label = metric.label ? ` ${metric.label}` : '';
                            return `${metric.name} was ${latestMetric.value || 'N/A'}${label} on ${date}`;
                          } else {
                            return `${metric.name} was rated as ${latestMetric.rating_text || 'N/A'} on ${date}`;
                          }
                        }
                        return '';
                      }).filter(desc => desc !== '').join('\n');
 
                      setMedicalConcernData({
                        subject: `${selectedCategory.category_name} Concern`,
                        description: description || `Medical concern for ${selectedCategory.category_name} category`,
                        date_identified: new Date().toISOString().split('T')[0],
                        status: 'Ongoing',
                        task: '',
                        service_provider: '',
                        due_date: new Date().toISOString().split('T')[0],
                        person_responsible: '',
                        metric_types_categories_id: selectedCategory.id,
                        task_id: null,
                        task_service_provider: '',
                        task_due_date: new Date().toISOString().split('T')[0],
                        task_person_responsible: '',
                        task_status: 'Ongoing',
                        task_recurring_months: '',
                        task_recurring: false,
                        task_sent_to_salesforce: false,
                        task_assigned_user_name: ''
                      });
                      setMedicalConcernId(null);
                    }
                    setSelectedMetricsForConcern([selectedMetricType]);
                    setCurrentStep(1);
                    setShowMedicalConcernsModal(true);
                  }}
                  style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: categoryMedicalConcerns[selectedCategory?.id] ? '#2c5282' : '#4a90e2',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  <span>
                    {categoryMedicalConcerns[selectedCategory?.id] ? 'View/Edit Medical Concern' : 'Add Medical Concern'}
                  </span>
                </button>
              </div>

              <div className="navigation-buttons">
                <div className="progress-text">
                  Parameter {getProgress().current} of {getProgress().total}
                </div>
                <button className="next-button" onClick={moveToNextMetric}>
                  Next Parameter
                </button>
              </div>
            </div>
          )}

          {/* Medical Concerns Modal */}
          <MedicalConcernsModal
            isOpen={showMedicalConcernsModal}
            onClose={handleMedicalConcernsModalClose}
            currentStep={currentStep}
            medicalConcernData={medicalConcernData}
            setMedicalConcernData={setMedicalConcernData}
            setMedicalConcernId={setMedicalConcernId}
            setCurrentStep={setCurrentStep}
            handleAddMedicalConcern={handleAddMedicalConcern}
            handleAddTask={handleAddTask}
            conciergeId={conciergeId}
            users={users}
            medicalConcernId={medicalConcernId}
          />

          {/* Consider Adding Modal */}
          {showConsiderModal && (
            <ConsiderAddingModal
              isOpen={true}
              onClose={() => setShowConsiderModal(false)}
              metricTypeName={selectedMetricType ? selectedMetricType.name : ''}
            />
          )}
        </div>
      </IonPage>
    </div>
  );
};

export default EditMetrics; 